import React, { useState, useEffect, useRef, useCallback } from "react";
import Model, { Providers } from "@tripian/model";
import { Backdrop, SvgIcons } from "@tripian/react";
import { TicketMap } from "@ticketevolution/seatmaps-client";
import { useIsMobile } from "../../../../hooks/useIsMobile";

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  tickets: Providers.Victory.TicketGroup[];
  eventId: number;
  venueId: string;
  configurationId: string;
  t: (value: Model.TranslationKey) => string;
}

const normalizeSection = (section: string) => section.trim().toLowerCase();

const TicketModal: React.FC<TicketModalProps> = ({
  isOpen,
  onClose,
  tickets,
  eventId,
  venueId,
  configurationId,
  t,
}) => {
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] =
    useState<Providers.Victory.TicketGroup | null>(null);
  const [allTicketGroups, setAllTicketGroups] = useState<
    Providers.Victory.TicketGroup[]
  >([]);
  const [filteredTicketGroups, setFilteredTicketGroups] = useState<
    Providers.Victory.TicketGroup[]
  >([]);
  const [quantityFilter, setQuantityFilter] = useState<number>(1);
  const [minPriceFilter, setMinPriceFilter] = useState<number>(0);
  const [maxPriceFilter, setMaxPriceFilter] = useState<number>(0);
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const ticketMapRef = useRef<any>(null);
  const ticketRefs = useRef<{ [section: string]: HTMLDivElement | null }>({});
  const isMobile = useIsMobile();

  const filterTicketGroups = useCallback(() => {
    return allTicketGroups
      .filter(tg => tg.quantity >= quantityFilter)
      .filter(tg => tg.retail_price <= maxPriceFilter)
      .filter(tg => tg.retail_price >= minPriceFilter)
      .filter(tg => typeFilter.size === 0 || typeFilter.has(tg.format));
  }, [
    allTicketGroups,
    quantityFilter,
    minPriceFilter,
    maxPriceFilter,
    typeFilter,
  ]);

  useEffect(() => {
    setFilteredTicketGroups(filterTicketGroups());
  }, [filterTicketGroups]);

  useEffect(() => {
    setAllTicketGroups(tickets);
    if (tickets.length > 0) {
      setMinPriceFilter(tickets[0].retail_price);
      setMaxPriceFilter(tickets[tickets.length - 1].retail_price);
    }
  }, [tickets]);

  const handleMapSelection = (sections: string[]) => {
    if (sections.length === 0) {
      setSelectedSection(null);
      setSelectedTicket(null);
      return;
    }
    const lastSection = sections[sections.length - 1].toLowerCase();
    setSelectedSection(lastSection);
    const found = filteredTicketGroups.find(
      ticket => ticket.tevo_section_name.trim().toLowerCase() === lastSection
    );
    setSelectedTicket(found || null);

    const foundKey = found
      ? `${normalizeSection(found.tevo_section_name)}__${found.row ? found.row.toString().toLowerCase() : ""}`
      : normalizeSection(lastSection);

    setTimeout(() => {
      const cardRef = ticketRefs.current[foundKey];
      if (cardRef) {
        cardRef.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  useEffect(() => {
    setFilteredTicketGroups(filterTicketGroups());
  }, [filterTicketGroups]);

  const handleCardClick = (ticket: Providers.Victory.TicketGroup) => {
    const normalized = normalizeSection(ticket.tevo_section_name);
    setSelectedSection(normalized);
    setSelectedTicket(ticket);

    if (
      ticketMapRef.current &&
      typeof ticketMapRef.current.updateTicketGroups === "function"
    ) {
      ticketMapRef.current.updateTicketGroups(filteredTicketGroups);
    }
  };

  useEffect(() => {
    if (
      ticketMapRef.current &&
      typeof ticketMapRef.current.updateTicketGroups === "function"
    ) {
      ticketMapRef.current.updateTicketGroups(filteredTicketGroups);
    }
  }, [filteredTicketGroups]);

  useEffect(() => {
    setSelectedSection(null);
    setSelectedTicket(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const allTypes = Array.from(new Set(allTicketGroups.map(tg => tg.format)));

  const handlePurchase = () => {
    if (selectedTicket) {
      const checkoutUrl = `http://checkout-evolution.tripian.com/checkout/payment/${eventId}/${selectedTicket.id}?quantity=${selectedQuantity}`;
      window.open(checkoutUrl, "_blank");
    }
  };

  return (
    <>
      <Backdrop show={isOpen} clicked={() => onClose()} zIndex={9999} />
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-3xl max-w-4xl w-full mx-2 md:mx-4 h-[98vh] md:h-[90vh] flex flex-col relative overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-primary-color">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <SvgIcons.Ticket2 className="w-6 h-6 mr-2" />
              {t("selectTickets")}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <SvgIcons.X fill="white" className="w-6 h-6" />
            </button>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex flex-row gap-2 md:gap-4 items-end flex-wrap">
              <div className="relative w-16 md:w-28 flex flex-col gap-2">
                <label className="block text-xs md:text-[13px] font-medium w-16 md:w-28">
                  {t("minPrice")}
                </label>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={minPriceFilter === 0 ? "" : minPriceFilter}
                    onChange={e => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val))
                        setMinPriceFilter(val === "" ? 0 : Number(val));
                    }}
                    className="border rounded-lg px-2 py-1 md:px-4 md:py-1 w-full pl-2 md:pl-4 pr-6 text-left appearance-none text-xs md:text-base"
                    placeholder="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    $
                  </span>
                </div>
              </div>
              <div className="relative w-16 md:w-28 flex flex-col gap-2">
                <label className="block text-xs md:text-[13px] font-medium w-16 md:w-28">
                  {t("maxPrice")}
                </label>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={maxPriceFilter === 0 ? "" : maxPriceFilter}
                    onChange={e => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val))
                        setMaxPriceFilter(val === "" ? 0 : Number(val));
                    }}
                    className="border rounded-lg px-2 py-1 md:px-4 md:py-1 w-full pl-2 md:pl-4 pr-6 text-left appearance-none text-xs md:text-base"
                    placeholder="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    $
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="block text-xs md:text-[13px] font-medium">
                  {t("minQuantity")}
                </label>
                <input
                  type="number"
                  value={quantityFilter}
                  min={1}
                  onChange={e => setQuantityFilter(Number(e.target.value))}
                  className="border rounded-lg px-1 py-1 md:px-2 md:py-1 w-12 md:w-24 text-center text-xs md:text-base"
                />
              </div>
              <div className="flex flex-col w-auto items-start">
                <label
                  className="block text-xs md:text-[13px] font-medium mb-[2px] text-center cursor-pointer select-none"
                  onClick={() => {
                    const allSelected = allTypes.every(type =>
                      typeFilter.has(type)
                    );
                    if (allSelected) {
                      setTypeFilter(new Set());
                    } else {
                      setTypeFilter(new Set(allTypes));
                    }
                  }}
                >
                  {t("type")}
                </label>
                <div className="flex flex-row gap-4 h-[40px] items-center">
                  {allTypes.map(type => (
                    <label
                      key={type}
                      className="flex items-center h-full gap-2 cursor-pointer select-none"
                    >
                      <div
                        className={`w-5 h-5 md:w-[22px] md:h-[22px] flex items-center justify-center rounded border-2 transition-colors duration-150 ${
                          typeFilter.has(type)
                            ? "border-primary-color bg-primary-color"
                            : "border-primary-color bg-white"
                        }`}
                        style={{ minWidth: 20, minHeight: 20 }}
                        onClick={() => {
                          setTypeFilter(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(type)) newSet.delete(type);
                            else newSet.add(type);
                            return newSet;
                          });
                        }}
                      >
                        {typeFilter.has(type) && (
                          <SvgIcons.Check2
                            fill="#fff"
                            className="w-3 h-3 md:w-4 md:h-4 text-white"
                          />
                        )}
                      </div>
                      <span className="text-xs md:text-base text-gray-700 font-medium">
                        {type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* TicketMap + Ticket List Responsive Layout */}
          <div className="flex flex-col md:flex-row w-full flex-1 min-h-0 bg-gray-50 rounded-lg relative gap-4">
            {/* TicketMap */}
            <div
              className="w-full md:w-1/2 h-[35vh] sm:h-[28vh] md:h-full md:min-h-[300px] flex-shrink-0 flex-grow-0 flex items-center justify-center"
              style={{ touchAction: "pan-x pan-y" }}
            >
              <TicketMap
                key={
                  filteredTicketGroups.map(t => t.tevo_section_name).join(",") +
                  (selectedSection || "")
                }
                ref={ticketMapRef}
                venueId={venueId}
                configurationId={configurationId}
                ticketGroups={filteredTicketGroups}
                selectedSections={selectedSection ? [selectedSection] : []}
                showControls={!isMobile}
                showLegend={!isMobile}
                mouseControlEnabled={true}
                showZoomHelper={true}
                onSelection={handleMapSelection}
              />
            </div>
            {/* Ticket List */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-2">
              <div className="space-y-3 sm:space-y-2">
                {filteredTicketGroups.map(ticket => (
                  <div
                    key={ticket.id}
                    ref={el =>
                      (ticketRefs.current[
                        `${normalizeSection(ticket.tevo_section_name)}__${ticket.row ? ticket.row.toString().toLowerCase() : ""}`
                      ] = el)
                    }
                    tabIndex={-1}
                    className={`border rounded-lg p-3 sm:p-2 cursor-pointer transition-all ${
                      selectedTicket?.id === ticket.id
                        ? "border-primary-color-500 bg-primary-color-100"
                        : "hover:border-gray-400"
                    }`}
                    onClick={() => handleCardClick(ticket)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-base">
                          {t("section")} {ticket.section}, {t("row")}{" "}
                          {ticket.row}
                        </h3>
                        <div className="space-y-1 mt-1 text-xs text-gray-600">
                          <p>
                            {t("available")}: {ticket.available_quantity}{" "}
                            {t("tickets")}
                          </p>
                          <p className="flex items-center">
                            <span
                              className={
                                ticket.eticket
                                  ? "text-green-600"
                                  : "text-gray-600"
                              }
                            >
                              {ticket.format}
                            </span>
                            {ticket.instant_delivery && (
                              <span className="ml-2 bg-green-400 text-green-800 text-xs px-1.5 py-0.5 rounded">
                                {t("instantDelivery")}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary-color">
                          $
                          {ticket.retail_price
                            ? ticket.retail_price.toFixed(2)
                            : ""}
                        </p>
                        {ticket.face_value_display && (
                          <p className="text-xs text-gray-500">
                            {t("faceValue")}: $
                            {ticket.face_value_display
                              ? ticket.face_value_display.toFixed(2)
                              : ""}
                          </p>
                        )}
                      </div>
                    </div>

                    {ticket.public_notes && (
                      <div className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                        <SvgIcons.Info3 className="w-3 h-3 flex-shrink-0 mt-[1.5px]" />
                        <p>{ticket.public_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {selectedTicket && (
            <div className="absolute bottom-0 left-0 right-0 border-t p-3 bg-white rounded-b-3xl shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <label className="text-xs font-medium">
                    {t("quantity")}:
                  </label>
                  <select
                    value={selectedQuantity}
                    onChange={e => setSelectedQuantity(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-sm w-14"
                  >
                    {selectedTicket.splits.map(split => (
                      <option key={split} value={split}>
                        {split}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-600">{t("total")}</p>
                  <p className="text-lg font-bold text-primary-color">
                    $
                    {selectedTicket.retail_price
                      ? (
                          selectedTicket.retail_price * selectedQuantity
                        ).toFixed(2)
                      : ""}
                  </p>
                </div>
              </div>
              <button
                onClick={handlePurchase}
                className="mt-3 w-full bg-primary-color-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-color transition-colors"
              >
                {t("purchaseTickets")}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketModal;
